// @ts-check
/** @typedef {import('../../lib/schemas').ResolvedImage} ResolvedImage */
import styles from './styles.module.css'

// ── The ONE shared image-render resolver (task #129) ────────────────────────
//
// components/Img/index.js (<Img>, client) and components/Img/Server.jsx
// (<ImgServer>, RSC) used to carry byte-for-byte DUPLICATED size/srcset/priority/
// reserveSpace logic — two copies that had to stay in lock-step by hand (the
// hydration-parity + reserveSpace-default footguns). This module is that logic,
// extracted ONCE as a pure function. Both leaves are now thin wrappers over it,
// so they render identical markup BY CONSTRUCTION and can never drift.
//
// PURE + RSC-SAFE: no hooks, no context, no Overmind, no browser APIs, no JSX.
// Imports NOTHING manifest-backed (only the descriptor shape + the styles
// module), so a Server Component can import it without tripping the RSC build
// boundary. The client/server split lives entirely in the two wrappers: the
// client one feeds `lcpSrc` from useLcpSrc() (context); the server one feeds the
// same value from its `lcpSrc` prop. The resolver just takes `lcpSrc`.

/**
 * SINGLE source of truth for the fill-mode CLS reservation default.
 *
 * Defined ONCE, here, and consumed by BOTH <Img> and <ImgServer>, so the two can
 * never diverge — this is the structural fix for the historical footgun where the
 * client and server components each carried their OWN `reserveSpace = true`
 * default and a Home/FfbNextGen RSC conversion had to remember to re-apply
 * `reserveSpace={false}` against a default that could silently drift between them.
 *
 * The value is TRUE: a dimensioned fill <Img> reserves its own aspect-ratio box
 * from first paint (no CLS). Callers whose PARENT slot already owns the box
 * (fixed-ratio letterboxes: ShopItem thumbnails, HomeSection / FfbNextGen heroes,
 * ProductTestimonialsV3 avatars) must OPT OUT with the explicit, greppable
 * `reserveSpace={false}` — the "don't reserve" path is deliberately explicit so a
 * client→server section conversion that forgets it is caught in review by the
 * missing prop, not by a silent layout change.
 *
 * NOTE: changing this constant changes rendered output for every fill caller that
 * does NOT pass an explicit `reserveSpace`. Do not flip it without auditing them.
 * @type {boolean}
 */
export const DEFAULT_RESERVE_SPACE = true

/**
 * Dev-only guardrail (task #67): both leaves must receive a pre-RESOLVED
 * descriptor ({ src }). If they get a raw Sanity image ref (has _type:'image' /
 * asset / _ref but no .src) the data source skipped the resolver (resolveImage /
 * resolvePageImages / normalizeOffers / the _app config walk) — the exact
 * regression that blanked the blog thumbnails + header logo. Scream in dev so a
 * missed seam is caught at author time. Zero prod cost (dead-stripped by the
 * NODE_ENV check). Side-effect only — does not touch the render model.
 * @param {ResolvedImage|null|undefined} image
 * @param {string} label  the calling component name for the message ('Img' | 'ImgServer')
 */
export function warnIfUnresolvedImage(image, label) {
  if (
    process.env.NODE_ENV !== 'production' &&
    image &&
    !image.src &&
    // @ts-ignore - probing an unresolved raw ref shape on the descriptor union
    (image._type === 'image' || image.asset || typeof image._ref === 'string')
  ) {
    console.error(
      `[${label}] received an UNRESOLVED image ref (no .src) — its data source was not ` +
        'run through the #67 resolver. Walk it in gSSP / the server data layer.',
      image
    )
  }
}

/**
 * @typedef {object} ImgRenderModel
 * @property {string} wrapperClassName  the composed wrapper <div> className
 * @property {React.CSSProperties} wrapperStyle  inline style for the wrapper <div>
 *   (reserved-ratio CSS vars + blur-up background / neutral fill)
 * @property {import('react').ImgHTMLAttributes<HTMLImageElement>} img
 *   the full attribute set for the inner <img> (src/srcSet/sizes/alt/width/height/
 *   decoding/loading/fetchPriority/style). Spread it onto <img>, then spread the
 *   wrapper's own `...rest` AFTER it (rest wins, matching the pre-#129 order).
 */

/**
 * Resolve a ResolvedImage descriptor + presentation props into the flat render
 * model both <Img> and <ImgServer> paint. Returns `null` when there is nothing to
 * draw (no image / no src) — the caller renders null.
 *
 * Eagerness contract (#118, unchanged by #129): the page LCP resolver is the
 * SINGLE authority. `effectivePriority = (lcpSrc != null && src === lcpSrc)`. The
 * `priority` prop is ADVISORY and is intentionally NOT a parameter of this
 * resolver — the wrappers accept it for API compatibility and drop it, so no call
 * site can force eager and flood the critical path with <link preload>s. Only the
 * one image whose src equals the threaded `lcpSrc` is eager + fetchPriority=high.
 *
 * @param {object} params
 * @param {ResolvedImage|null|undefined} params.image  a pre-RESOLVED descriptor
 * @param {string} [params.sizes]      responsive `sizes` attr (default '100vw')
 * @param {string|null} [params.lcpSrc]  the page LCP hero src (lib/lcpImage.js).
 *   When it equals `image.src`, eager is forced — the ONE eagerness authority.
 * @param {import('react').CSSProperties['objectFit']} [params.objectFit]  (default 'contain')
 * @param {string} [params.layout]     'fill' | 'responsive' (default 'fill')
 * @param {boolean} [params.reserveSpace]  fill-mode CLS control (default
 *   DEFAULT_RESERVE_SPACE = true; see that constant for the opt-out contract)
 * @param {string} [params.className]  applied to the wrapper
 * @param {string} [params.alt]        overrides image.alt
 * @returns {ImgRenderModel|null}
 */
export function resolveImgRender({
  image,
  sizes = '100vw',
  lcpSrc = null,
  objectFit = 'contain',
  layout = 'fill',
  reserveSpace = DEFAULT_RESERVE_SPACE,
  className,
  alt,
}) {
  if (!image || !image.src) return null

  const { src, srcSet, width, height, placeholderUrl } = image
  const resolvedAlt = alt != null ? alt : image.alt || ''

  // Reserve the box for fill images so they don't shift layout on load (CLS).
  // In fill mode the <img> is position:absolute and contributes no height, so
  // the wrapper must claim the aspect-ratio box from first paint. Prefer the
  // descriptor's raw dims for exactness; else invert the descriptor aspectRatio.
  // When neither is known (external/backend image) leave it undefined and the
  // wrapper falls back to filling its parent slot — no reserved ratio.
  const isFill = layout === 'fill'
  const hasDims = width != null && height != null && height > 0
  const ratio = hasDims
    ? width / height
    : image.aspectRatio != null && image.aspectRatio > 0
    ? image.aspectRatio
    : null
  const ratioPadding = hasDims ? (height / width) * 100 : ratio ? 100 / ratio : null
  const reservesRatio = Boolean(isFill && reserveSpace && ratio)

  /** @type {React.CSSProperties} */
  const imgStyle = isFill
    ? {
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        width: '100%',
        height: '100%',
        objectFit,
      }
    : { width: '100%', height: 'auto', objectFit }

  // Effective priority: ONLY the page's resolver-chosen LCP image (lcpSrc match)
  // is eager + fetchPriority=high. A hard-coded priority prop does NOT force
  // eager (#118 FCP fix): Next 14 / React-Float auto-hoists a <link rel=preload
  // as=image> for every eager <img>, so section-level priority produced ~8 home
  // hero preloads that out-raced the render-blocking route CSS. Restricting eager
  // to the single LCP hero collapses that flood to one preload. The client <Img>
  // supplies lcpSrc from useLcpSrc() (context); <ImgServer> from its lcpSrc prop —
  // same computed value, so the eager/lazy decision is byte-identical.
  const isLcp = lcpSrc != null && src === lcpSrc
  const effectivePriority = isLcp
  /** @type {import('react').ImgHTMLAttributes<HTMLImageElement>} */
  const loadingProps = effectivePriority
    ? { loading: 'eager', fetchPriority: 'high' }
    : { loading: 'lazy' }

  // Compose the wrapper class: base + fill-ratio-box OR fluid fill + passthrough.
  const sizingClass = reservesRatio ? styles.fill : styles.fluid
  const wrapperClassName = className
    ? `${styles.imgWrapper} ${sizingClass} ${className}`
    : `${styles.imgWrapper} ${sizingClass}`

  // Dynamic declarations that rode styled-component interpolations live inline:
  // the reserved-ratio CSS vars (consumed by .fill + its ::before) and the blur-up
  // placeholder background (or the neutral fallback fill).
  /** @type {React.CSSProperties} */
  const wrapperStyle = {}
  if (reservesRatio) {
    wrapperStyle['--img-aspect-ratio'] = `${ratio}`
    wrapperStyle['--img-ratio-padding'] = `${(ratioPadding ?? 0).toFixed(4)}%`
  }
  if (placeholderUrl) {
    wrapperStyle.backgroundImage = `url("${placeholderUrl}")`
    wrapperStyle.backgroundSize = objectFit === 'contain' ? 'contain' : 'cover'
    wrapperStyle.backgroundPosition = 'center'
    wrapperStyle.backgroundRepeat = 'no-repeat'
  } else {
    wrapperStyle.backgroundColor = 'rgba(0,0,0,0.04)'
  }

  return {
    wrapperClassName,
    wrapperStyle,
    img: {
      src,
      srcSet: srcSet || undefined,
      sizes: srcSet ? sizes : undefined,
      alt: resolvedAlt,
      width: !isFill && width != null ? width : undefined,
      height: !isFill && height != null ? height : undefined,
      decoding: 'async',
      ...loadingProps,
      style: imgStyle,
    },
  }
}
